import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { RefreshCw, Trophy, Users, MessageCircle, ShoppingBag, CreditCard, Sparkles } from "lucide-react";

const VARIANT_COLORS = ["#047857", "#fbbf24", "#10b981", "#f59e0b", "#059669", "#ea580c"];

const EXPERIMENT_LABELS = {
  "landing-headline": "Landing headline",
  "landing-cta": "Hero CTA button",
  "subscribe-banner-emphasis": "Subscribe banner emphasis",
  "result-urgency-tone": "Result urgency tone",
  "whatsapp-message-style": "WhatsApp message style",
};

export default function ExperimentsHotBar() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshedAt, setRefreshedAt] = useState(null);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/admin/experiments");
      setData(d);
      setRefreshedAt(new Date());
    } catch {
      setData({ experiments: [] });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 60_000); // auto refresh every minute
    return () => clearInterval(id);
  }, []);

  const experiments = data?.experiments || [];

  return (
    <div className="space-y-6" data-testid="experiments-hotbar">
      <Card className="rounded-2xl border-emerald-100">
        <CardContent className="p-5 flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-emerald-700 font-semibold">
              <Sparkles className="w-3.5 h-3.5" /> Experiments live monitor
            </div>
            <h2 className="font-display text-xl text-emerald-950 mt-1">
              Which messaging is winning?
            </h2>
            <p className="text-sm text-emerald-900/60 mt-1">
              Data is attributed by anonymous client-id, so visitors who see a variant on landing
              and convert later are correctly counted. Auto-refreshes every minute.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-emerald-900/60">
              {refreshedAt ? `Updated ${refreshedAt.toLocaleTimeString()}` : ""}
            </span>
            <Button
              variant="outline"
              onClick={load}
              className="rounded-full border-emerald-200 text-emerald-900 hover:bg-emerald-50"
              data-testid="hotbar-refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      {error ? (
        <Card className="rounded-2xl border-red-200 bg-red-50/70" data-testid="hotbar-error">
          <CardContent className="p-4 text-sm text-red-800">{error}</CardContent>
        </Card>
      ) : null}

      {loading && experiments.length === 0 ? (
        <div className="text-emerald-700 text-sm">Loading experiment data…</div>
      ) : experiments.length === 0 ? (
        <Card className="rounded-2xl border-emerald-100">
          <CardContent className="p-8 text-center text-emerald-900/70" data-testid="hotbar-empty">
            <div className="font-medium text-emerald-950 mb-2">No exposures recorded yet</div>
            <p className="text-sm">
              Once visitors land on a page with an active experiment, you'll see live variant
              metrics here. Each variant gets at least a few hundred exposures before results
              are statistically meaningful.
            </p>
          </CardContent>
        </Card>
      ) : (
        experiments.map((e) => <ExperimentBlock key={e.experiment_key} exp={e} />)
      )}
    </div>
  );
}

function ExperimentBlock({ exp }) {
  // Pick the winning variant by purchase rate first, then checkout rate.
  const winner = [...exp.variants].sort((a, b) => {
    if (b.purchase_rate !== a.purchase_rate) return b.purchase_rate - a.purchase_rate;
    if (b.checkout_rate !== a.checkout_rate) return b.checkout_rate - a.checkout_rate;
    return b.whatsapp_rate - a.whatsapp_rate;
  })[0];
  const totalExposed = exp.total_exposed || 0;
  // Don't crown a winner unless we have ≥2 variants and a meaningful sample.
  const STAT_SANITY_THRESHOLD = 50;
  const winnerHasSignal =
    winner && (winner.purchase_rate > 0 || winner.checkout_rate > 0 || winner.whatsapp_rate > 0);
  const showWinner =
    exp.variants.length > 1 && totalExposed >= STAT_SANITY_THRESHOLD && winnerHasSignal;

  return (
    <Card className="rounded-2xl border-emerald-100" data-testid={`experiment-block-${exp.experiment_key}`}>
      <CardContent className="p-6">
        <div className="flex items-end justify-between flex-wrap gap-2 mb-4">
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-emerald-700 font-semibold">
              {exp.experiment_key}
            </div>
            <h3 className="font-display text-lg text-emerald-950">
              {EXPERIMENT_LABELS[exp.experiment_key] || exp.experiment_key}
            </h3>
          </div>
          <div className="flex items-center gap-1 text-xs text-emerald-900/70">
            <Users className="w-3.5 h-3.5" /> {totalExposed} total exposures
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {exp.variants.map((v, i) => {
            const isWinner = showWinner && v.variant === winner.variant;
            const color = VARIANT_COLORS[i % VARIANT_COLORS.length];
            const sharePct = totalExposed ? Math.round((v.exposed / totalExposed) * 100) : 0;
            return (
              <div
                key={v.variant}
                className={`rounded-xl border p-4 ${
                  isWinner ? "border-amber-300 bg-amber-50/50 ring-2 ring-amber-200/60" : "border-emerald-100 bg-white"
                }`}
                data-testid={`variant-${exp.experiment_key}-${v.variant}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: color }}
                      aria-hidden
                    />
                    <span className="font-medium text-emerald-950 text-sm">{v.variant}</span>
                  </div>
                  {isWinner ? (
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] font-bold text-amber-800">
                      <Trophy className="w-3 h-3" /> leading
                    </span>
                  ) : null}
                </div>
                <div className="text-xs text-emerald-900/60 mb-3">
                  {v.exposed} users · {sharePct}% of traffic
                </div>
                <div className="space-y-2.5">
                  <Metric
                    icon={MessageCircle}
                    label="WhatsApp"
                    value={`${v.whatsapp_rate}%`}
                    sub={`${v.whatsapp_clicks} clicks`}
                    pct={v.whatsapp_rate}
                    color={color}
                    testid={`metric-whatsapp-${exp.experiment_key}-${v.variant}`}
                  />
                  <Metric
                    icon={ShoppingBag}
                    label="Checkout"
                    value={`${v.checkout_rate}%`}
                    sub={`${v.checkout_starts} started`}
                    pct={v.checkout_rate}
                    color={color}
                    testid={`metric-checkout-${exp.experiment_key}-${v.variant}`}
                  />
                  <Metric
                    icon={CreditCard}
                    label="Purchase"
                    value={`${v.purchase_rate}%`}
                    sub={`${v.purchases} paid`}
                    pct={v.purchase_rate}
                    color={color}
                    isHero
                    testid={`metric-purchase-${exp.experiment_key}-${v.variant}`}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

function Metric({ icon: Icon, label, value, sub, pct, color, isHero, testid }) {
  // Cap visual bar fill at 100, but if rate >100 (impossible normally) just clamp.
  const fill = Math.max(0, Math.min(100, pct));
  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between text-xs">
        <span className={`flex items-center gap-1 ${isHero ? "text-emerald-900 font-semibold" : "text-emerald-900/70"}`}>
          <Icon className="w-3 h-3" /> {label}
        </span>
        <span className={isHero ? "font-display text-base text-emerald-950" : "font-medium text-emerald-900"}>
          {value} <span className="text-[10px] text-emerald-900/50 font-normal">· {sub}</span>
        </span>
      </div>
      <div className="mt-1 h-1.5 bg-emerald-50 rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all"
          style={{ width: `${fill}%`, backgroundColor: color }}
        />
      </div>
    </div>
  );
}
